import { useState } from "react";
import { usePreisrunde, usePreisrundeAnkuendigen, datum, eur } from "../hooks/useVertraege";
import type { PreisrundeZeile } from "../hooks/useVertraege";

/**
 * Preisrunde — der Hebel gegen steigende Lohn-, Sprit- und Entsorgungskosten.
 *
 * § 8 des Vertrages lässt eine Anpassung frühestens zwölf Monate nach Beginn
 * und nur zum Beginn eines neuen Vertragsjahres zu, angekündigt mit acht Wochen
 * Vorlauf; über 5 % bekommt der Kunde ein Sonderkündigungsrecht. Genau diese
 * drei Regeln rechnet die Vorschau aus, statt sie dem Gedächtnis zu überlassen.
 *
 * Angekündigt wird — nicht angepasst. Der neue Preis entsteht später als
 * Nachtrag am Vertrag. Diese Reihenfolge ist der Kern von § 8.
 */
export default function Preisrunde() {
  const [prozent, setProzent] = useState(0);
  const [offen, setOffen] = useState(false);
  const [gewaehlt, setGewaehlt] = useState<Record<string, boolean>>({});
  const [meldung, setMeldung] = useState("");
  const [fehler, setFehler] = useState("");
  const { data: p } = usePreisrunde(prozent, offen && prozent > 0);
  const ankuendigen = usePreisrundeAnkuendigen();

  const kandidaten = (p?.vertraege || []).filter(
    z => z.vorlauf_reicht && !z.bereits_angekuendigt);
  const ausgewaehlt = kandidaten.filter(z => gewaehlt[z.id]);
  const mehr = ausgewaehlt.reduce((s, z) => s + Number(z.differenz_netto), 0);

  return (
    <>
      <div className="pf-seclabel" id="preisrunde" style={{ scrollMarginTop: 16 }}>Preisanpassung</div>
      {!offen ? (
        <button className="pf-card" style={{ width: "100%", textAlign: "left", cursor: "pointer" }}
          onClick={() => setOffen(true)}>
          <div style={{ fontWeight: 700 }}>Preisrunde vorbereiten</div>
          <div style={{ fontSize: 12.5, color: "var(--pf-ink-3)", marginTop: 3 }}>
            Sehen, welche Verträge sich anpassen lassen und was es bringt →
          </div>
        </button>
      ) : (
        <div className="pf-card">
          <label className="pf-field"><span>Anhebung in Prozent</span>
            <input className="pf-input" type="number" step="0.5" min={0} max={25} value={prozent}
              onChange={e => { setProzent(Number(e.target.value) || 0); setGewaehlt({}); }} /></label>

          {prozent <= 0 && (
            <div style={{ fontSize: 13, color: "var(--pf-ink-3)", marginTop: 10 }}>
              Trag einen Prozentsatz ein, dann rechnet die Vorschau je Vertrag durch.
            </div>
          )}

          {p && prozent > 0 && (
            <>
              <div style={{ marginTop: 12, fontSize: 13.5, lineHeight: 1.7 }}>
                <div><strong>{p.vertraege.length}</strong> Verträge im nächsten halben Jahr anpassbar</div>
                <div>Jahreswert {eur(p.summe_alt)} → <strong>{eur(p.summe_neu)}</strong>
                  {" "}(+{eur(p.summe_mehr)})</div>
                {p.mit_sonderkuendigung > 0 && (
                  <div style={{ color: "var(--pf-ink-2)" }}>
                    {p.mit_sonderkuendigung} davon über 5 % — dort entsteht ein Sonderkündigungsrecht.
                  </div>
                )}
                {p.ohne_vorlauf > 0 && (
                  <div style={{ color: "var(--pf-ink-2)" }}>
                    {p.ohne_vorlauf} ohne die nötigen acht Wochen Vorlauf — die kommen erst im
                    nächsten Vertragsjahr dran.
                  </div>
                )}
              </div>

              <div style={{ marginTop: 12 }}>
                {p.vertraege.map(z => (
                  <PreisZeile key={z.id} z={z} gewaehlt={!!gewaehlt[z.id]}
                    onToggle={() => setGewaehlt(g => ({ ...g, [z.id]: !g[z.id] }))} />
                ))}
              </div>

              {ausgewaehlt.length > 0 && (
                <button className="pf-btn" style={{ marginTop: 12 }} disabled={ankuendigen.isPending}
                  onClick={async () => {
                    setFehler(""); setMeldung("");
                    try {
                      const r = await ankuendigen.mutateAsync({
                        vertraege: ausgewaehlt.map(z => ({
                          id: z.id, prozent: Number(z.prozent_tatsaechlich),
                          wirksam_zum: z.wirksam_zum,
                        })),
                      });
                      setMeldung(`${r.angekuendigt.length} angekündigt`
                        + (r.fehler.length ? `, ${r.fehler.length} nicht` : "") + ".");
                      setGewaehlt({});
                    } catch (e) { setFehler(e instanceof Error ? e.message : "Fehlgeschlagen."); }
                  }}>
                  {ankuendigen.isPending ? "Kündige an…"
                    : `${ausgewaehlt.length} Verträge ankündigen (+${eur(mehr)} im Jahr)`}
                </button>
              )}
              {meldung && <div style={{ fontSize: 12.5, marginTop: 8, color: "var(--pf-ink-3)" }}>{meldung}</div>}
              {fehler && <div style={{ fontSize: 12.5, marginTop: 8, color: "var(--pf-red)" }}>{fehler}</div>}
              <div style={{ fontSize: 12, color: "var(--pf-ink-3)", marginTop: 10, lineHeight: 1.5 }}>
                Angekündigt wird jetzt, angepasst später: der neue Preis entsteht als Nachtrag am
                Vertrag, wenn der Termin da ist. So verlangt es § 8.
              </div>
            </>
          )}
        </div>
      )}
    </>
  );
}

function PreisZeile({ z, gewaehlt, onToggle }: {
  z: PreisrundeZeile; gewaehlt: boolean; onToggle: () => void;
}) {
  const sperre = !z.vorlauf_reicht || !!z.bereits_angekuendigt;
  return (
    <label style={{
      display: "flex", gap: 10, alignItems: "flex-start", padding: "8px 0",
      borderTop: "1px solid var(--pf-line)", opacity: sperre ? 0.55 : 1,
      cursor: sperre ? "default" : "pointer",
    }}>
      <input type="checkbox" checked={gewaehlt} disabled={sperre} onChange={onToggle}
        style={{ marginTop: 3, width: 18, height: 18 }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 600, fontSize: 14 }}>{z.kunde_name}</div>
        <div style={{ fontSize: 12.5, color: "var(--pf-ink-3)" }}>
          {z.nummer} · zum {datum(z.wirksam_zum)} · {eur(z.jahres_netto_alt)} →{" "}
          {eur(z.jahres_netto_neu)} ({z.prozent_tatsaechlich} %)
        </div>
        {z.bereits_angekuendigt && (
          <div style={{ fontSize: 12.5, color: "var(--pf-ink-3)" }}>
            bereits angekündigt zum {datum(z.bereits_angekuendigt)}
          </div>
        )}
        {!z.vorlauf_reicht && !z.bereits_angekuendigt && (
          <div style={{ fontSize: 12.5, color: "var(--pf-ink-2)" }}>
            nur {z.vorlauf_tage} Tage Vorlauf — § 8 verlangt acht Wochen
          </div>
        )}
        {z.sonderkuendigung && !sperre && (
          <div style={{ fontSize: 12.5, color: "var(--pf-ink-2)" }}>
            über 5 % — der Kunde bekommt ein Sonderkündigungsrecht
          </div>
        )}
      </div>
      <div style={{ whiteSpace: "nowrap", fontWeight: 700, fontSize: 13.5 }}>
        +{eur(z.differenz_netto)}
      </div>
    </label>
  );
}
